/**
 * Corporate Intelligence Tool Configurations
 *
 * Tools for corporate family tree research and M&A analysis.
 */ import { defineToolConfig, formatCount, hasProperty } from '../tool-display-config';
// ============================================================================
// Progress Steps
// ============================================================================
const corporateFamilyTreeProgressSteps = [
    {
        id: 'enrichment',
        label: 'Enriching Data',
        loadingText: 'Enriching company information...'
    },
    {
        id: 'discovery',
        label: 'Discovery',
        loadingText: 'Discovering corporate relationships...'
    },
    {
        id: 'validation',
        label: 'Validating',
        loadingText: 'Validating subsidiaries...'
    },
    {
        id: 'complete',
        label: 'Complete',
        loadingText: 'Finalizing results...'
    }
];
// ============================================================================
// Helper Functions
// ============================================================================
function isCorporateFamilyTreeSuccess(output) {
    return typeof output === 'object' && output !== null && 'targetCompany' in output && 'mermaidDiagram' in output && 'confidenceScore' in output;
}
function isCorporateFamilyTreeError(output) {
    return typeof output === 'object' && output !== null && 'message' in output && 'code' in output && !('targetCompany' in output);
}
// ============================================================================
// Tool Configuration
// ============================================================================
const corporateFamilyTreeConfig = defineToolConfig({
    id: 'corporate-family-tree',
    aliases: [
        'corporateFamilyTree',
        'corporateFamilyTreeTool'
    ],
    progressSteps: corporateFamilyTreeProgressSteps,
    formatSuccess: (output)=>{
        if (isCorporateFamilyTreeError(output)) {
            return `Research failed: ${output.message}`;
        }
        if (isCorporateFamilyTreeSuccess(output)) {
            const company = output.targetCompany.name;
            const subsidiaryCount = output.subsidiaries?.length ?? 0;
            const hasParent = !!output.parentCompany || (output.parentHierarchy?.length ?? 0) > 0;
            const confidence = Math.round(output.confidenceScore);
            const parts = [
                `Analyzed ${company}`
            ];
            if (hasParent) parts.push('parent identified');
            if (subsidiaryCount > 0) parts.push(`${formatCount(subsidiaryCount, 'subsidiary', 'subsidiaries')}`);
            parts.push(`(${confidence}% confidence)`);
            return parts.join(', ');
        }
        return 'Corporate research completed';
    },
    formatError: (error)=>{
        if (hasProperty(error, 'message')) {
            return `Corporate research failed: ${error.message}`;
        }
        return 'Failed to research corporate structure';
    },
    formatInput: (input)=>{
        if (!input?.companyName) return [];
        const fields = [
            {
                label: 'Company',
                value: input.companyName
            }
        ];
        if (input.uei) fields.push({
            label: 'UEI',
            value: input.uei
        });
        if (input.yearsBack) fields.push({
            label: 'History',
            value: `${input.yearsBack} years`
        });
        return fields;
    },
    isValidOutput: (output)=>{
        return isCorporateFamilyTreeSuccess(output) || isCorporateFamilyTreeError(output);
    },
    isErrorResult: (output)=>isCorporateFamilyTreeError(output)
});
// ============================================================================
// Export
// ============================================================================
export const corporateToolConfigs = [
    corporateFamilyTreeConfig
];
// Also export type guards for use in specialized renderers
export { isCorporateFamilyTreeSuccess, isCorporateFamilyTreeError };
